import LoadingButton from "@mui/lab/LoadingButton"
import Button from "@mui/material/Button"
import Dialog from "@mui/material/Dialog"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import FormControl from "@mui/material/FormControl"
import InputLabel from "@mui/material/InputLabel"
import MenuItem from "@mui/material/MenuItem"
import Paper from "@mui/material/Paper"
import Portal from "@mui/material/Portal"
import Select from "@mui/material/Select"
import Slide from "@mui/material/Slide"
import TextField from "@mui/material/TextField"
import Typography from "@mui/material/Typography"
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker"
import { addHours } from "date-fns"
import { useEffect, useState } from "react"
import { useSelector } from "react-redux"

import { parseApiErrorMessage } from "../../../common/apiErrors"
import { useAuthorizedAxiosClient } from "../../../common/axios"
import Column from "../../../common/components/Column"
import Row from "../../../common/components/Row"
import { formatPhone } from "../../../common/utils"
import {
  ACCOUNT_ROLES,
  isConcierge,
  isExecutiveCaller,
  isTeamMember,
  selectAccountRole,
} from "../../users/accountRole"
import { selectProfile } from "../../users/reducer"
import MissedYouEmailButton from "../calling/MissedYouEmailButton"
import MissedYouSmsButton from "../calling/MissedYouSmsButton"
import {
  CALL_DISPOSITIONS,
  SELECTABLE_CALL_DISPOSITIONS,
  getCallDispositionLabel,
} from "../calling/callDispositions"
import DialogHeader from "../components/DialogHeader"

export { CALL_DISPOSITIONS, getCallDispositionLabel }

export function describeCallAttemptActor(profile) {
  const name = [profile?.first_name, profile?.last_name]
    .filter(Boolean)
    .join(" ")
    .trim()
  if (isConcierge(profile)) {
    return {
      roleLabel: "Concierge",
      displayName: name || "Concierge",
    }
  }
  if (isTeamMember(profile)) {
    return {
      roleLabel: "Team member",
      displayName: name || "Team member",
    }
  }
  if (isExecutiveCaller(profile)) {
    return {
      roleLabel: "Executive caller",
      displayName: name || "Executive caller",
    }
  }
  if (selectAccountRole(profile) === ACCOUNT_ROLES.owner) {
    return {
      roleLabel: "You (account owner)",
      displayName: name || "You",
    }
  }
  return {
    roleLabel: "You",
    displayName: name || "You",
  }
}

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

const CALL_LOG_DIALOG_Z_INDEX = 1700
const CALL_LOG_MENU_Z_INDEX = 1800
const CALL_LOG_PICKER_Z_INDEX = 1900
const CALL_LOG_PANEL_WIDTH = 400

function defaultCallbackWindow() {
  const start = addHours(new Date(), 1)
  return { start, end: addHours(start, 1) }
}

export function defaultDispositionFromDialStatus(dialStatus) {
  switch (dialStatus) {
    case "busy":
      return "busy"
    case "no-answer":
      return "no_answer"
    case "failed":
    case "canceled":
      return "other"
    default:
      return "no_answer"
  }
}

/**
 * Resolve the Twilio Call SID for an ended attempt.
 * Prefers the SID from the Voice SDK; falls back to the candidate's latest call row.
 * Retries briefly because the voice-request webhook may still be writing the row.
 */
export async function resolveCallSidForAttempt(
  axios,
  callSid,
  contact,
  { retries = 3, delayMs = 250 } = {}
) {
  if (callSid && String(callSid).startsWith("CA")) {
    return callSid
  }
  const latestUrl = contact?.isLead
    ? "/crm/api/leads/calls/latest/"
    : contact?.id
    ? `/crm/api/candidates/${contact.id}/calls/latest/`
    : null
  if (!latestUrl) {
    return null
  }
  const latestParams = contact?.isLead
    ? {
        lead_source: contact.leadSource,
        lead_ref_id: contact.leadRefId,
      }
    : undefined
  for (let attempt = 0; attempt < retries; attempt += 1) {
    try {
      const { data } = await axios.get(latestUrl, { params: latestParams })
      if (data.twilio_sid) {
        return data.twilio_sid
      }
    } catch (_) {
      // Call row may not exist yet while Twilio webhooks catch up.
    }
    if (attempt < retries - 1) {
      await sleep(delayMs)
    }
  }
  return null
}

/**
 * Optionally refine disposition from Twilio dial_status without blocking the dialog.
 */
export async function fetchDialStatusForCall(axios, callSid) {
  if (!callSid) {
    return null
  }
  try {
    const { data } = await axios.get(
      `/crm/api/calls/${encodeURIComponent(callSid)}/`
    )
    return data.dial_status || null
  } catch (_) {
    return null
  }
}

/**
 * `sidePanel` docks the form against the right edge instead of centering it, so
 * the list underneath stays readable while notes are typed. Used for Power
 * Dialer sessions, where it sits flush against the Power Dialer sidebar.
 */
export default function CallAttemptLogDialog({
  open,
  onClose,
  candidate,
  callSid,
  initialDisposition = "no_answer",
  sidePanel = false,
}) {
  const [disposition, setDisposition] = useState(initialDisposition)
  const [notes, setNotes] = useState("")
  const [callbackStart, setCallbackStart] = useState(null)
  const [callbackEnd, setCallbackEnd] = useState(null)
  const [isSaving, setIsSaving] = useState(false)
  const [saveError, setSaveError] = useState(null)
  const [resolvedCallSid, setResolvedCallSid] = useState(callSid || null)
  const axios = useAuthorizedAxiosClient()
  const profile = useSelector(selectProfile)
  const actor = describeCallAttemptActor(profile)

  useEffect(() => {
    if (!open) {
      return
    }
    let cancelled = false
    setDisposition(initialDisposition || "no_answer")
    setNotes("")
    setSaveError(null)
    setResolvedCallSid(callSid || null)
    setCallbackStart(null)
    setCallbackEnd(null)
    if ((initialDisposition || "no_answer") === "callback") {
      const window = defaultCallbackWindow()
      setCallbackStart(window.start)
      setCallbackEnd(window.end)
    }

    async function linkCallAndPrefill() {
      const sid = await resolveCallSidForAttempt(axios, callSid, candidate)
      if (cancelled) {
        return
      }
      if (sid) {
        setResolvedCallSid(sid)
        const dialStatus = await fetchDialStatusForCall(axios, sid)
        // Keep a caller-supplied preselect (e.g. answering-machine detection
        // saw voicemail); only the generic default gets refined from Twilio.
        const usingDefault = (initialDisposition || "no_answer") === "no_answer"
        if (!cancelled && dialStatus && usingDefault) {
          setDisposition(defaultDispositionFromDialStatus(dialStatus))
        }
      }
    }

    linkCallAndPrefill()
    return () => {
      cancelled = true
    }
  }, [open, callSid, candidate, initialDisposition, axios])

  function handleSoftClose() {
    onClose()
  }

  function handleDispositionChanged(value) {
    setDisposition(value)
    if (value === "callback" && !callbackStart) {
      const window = defaultCallbackWindow()
      setCallbackStart(window.start)
      setCallbackEnd(window.end)
    }
  }

  function handleCallbackStartChanged(value) {
    setCallbackStart(value)
    if (value && (!callbackEnd || callbackEnd <= value)) {
      setCallbackEnd(addHours(value, 1))
    }
  }

  async function handleSave() {
    const isLead = Boolean(candidate?.isLead)
    if (!isLead && !candidate?.id) {
      setSaveError("Candidate is missing. Please try again.")
      return
    }
    if (isLead && (!candidate?.leadSource || !candidate?.leadRefId)) {
      setSaveError("Lead details are missing. Please try again.")
      return
    }
    if (disposition === "callback" && (!callbackStart || !callbackEnd)) {
      setSaveError("Choose the date and time window to call this contact back.")
      return
    }
    setIsSaving(true)
    setSaveError(null)
    try {
      const sid =
        resolvedCallSid ||
        (await resolveCallSidForAttempt(axios, callSid, candidate))
      if (sid) {
        setResolvedCallSid(sid)
      }
      const payload = {
        twilio_sid: sid || null,
        disposition,
        notes: notes.trim() || null,
        ...(disposition === "callback"
          ? {
              callback_datetime: callbackStart.toISOString(),
              callback_end_datetime: callbackEnd.toISOString(),
            }
          : {}),
      }
      const { data } = isLead
        ? await axios.post("/crm/api/leads/calls/", {
            ...payload,
            lead_source: candidate.leadSource,
            lead_ref_id: candidate.leadRefId,
            lead_name: candidate.candidate_name || "",
            lead_phone: candidate.phone || "",
          })
        : await axios.post(
            `/crm/api/candidates/${candidate.id}/calls/`,
            payload
          )
      onClose(data)
    } catch (error) {
      setSaveError(
        parseApiErrorMessage(
          error?.response?.data,
          "Could not save this call attempt yet. Wait a moment and try again."
        )
      )
    } finally {
      setIsSaving(false)
    }
  }

  const contactLabel = candidate?.isLead
    ? candidate?.candidate_name || "this lead"
    : candidate?.candidate_name || "this candidate"
  const timelineLabel = candidate?.isLead ? "activity log" : "timeline"
  const contactName = candidate?.candidate_name || ""
  const brokerageName = candidate?.company_name || ""
  const contactPhone = candidate?.phone ? formatPhone(candidate.phone) : ""
  const hasContactSummary = Boolean(
    contactName || brokerageName || contactPhone
  )

  const body = (
    <>
      <DialogHeader title="Log call attempt" onClose={handleSoftClose} />
      <DialogContent sx={sidePanel ? { flex: 1, minHeight: 0 } : undefined}>
        <Column gap={2.5} sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            This call was not connected. Log the disposition and an optional
            note so it stays on {contactLabel}
            {"'s"} {timelineLabel}.
          </Typography>
          {hasContactSummary ? (
            <Column
              gap={0.25}
              sx={{
                p: 1.5,
                borderRadius: 2,
                bgcolor: "colors.gray50",
                border: "1px solid",
                borderColor: "colors.gray200",
              }}
            >
              {contactName ? (
                <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                  {contactName}
                </Typography>
              ) : null}
              {brokerageName ? (
                <Typography variant="body2" color="text.secondary">
                  {brokerageName}
                </Typography>
              ) : null}
              {contactPhone ? (
                <Typography variant="body2" color="text.secondary">
                  {contactPhone}
                </Typography>
              ) : null}
            </Column>
          ) : null}
          <Typography variant="body1">
            Attempted by{" "}
            <Typography component="span" fontWeight={600}>
              {actor.displayName}
            </Typography>
            {actor.roleLabel ? ` · ${actor.roleLabel}` : null}
          </Typography>
          <FormControl fullWidth required>
            <InputLabel id="call-disposition-label">Disposition</InputLabel>
            <Select
              labelId="call-disposition-label"
              label="Disposition"
              value={disposition}
              onChange={(e) => handleDispositionChanged(e.target.value)}
              MenuProps={{
                sx: { zIndex: CALL_LOG_MENU_Z_INDEX },
                PaperProps: { sx: { zIndex: CALL_LOG_MENU_Z_INDEX } },
              }}
            >
              {SELECTABLE_CALL_DISPOSITIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {disposition === "dnc" ? (
            <Typography variant="body2" color="error">
              This contact will be marked as Do Not Call (DNC).
            </Typography>
          ) : null}
          {disposition === "callback" ? (
            <Column gap={1.5}>
              <Typography variant="body2" color="text.secondary">
                Set the window when this contact can be called back. A call task
                will be created for {actor.displayName}.
              </Typography>
              <DateTimePicker
                label="Call back from"
                value={callbackStart}
                onChange={handleCallbackStartChanged}
                disablePast
                slotProps={{
                  textField: { required: true, fullWidth: true },
                  popper: { sx: { zIndex: CALL_LOG_PICKER_Z_INDEX } },
                  dialog: { sx: { zIndex: CALL_LOG_PICKER_Z_INDEX } },
                }}
              />
              <DateTimePicker
                label="Call back until"
                value={callbackEnd}
                onChange={setCallbackEnd}
                disablePast
                minDateTime={callbackStart || undefined}
                slotProps={{
                  textField: { required: true, fullWidth: true },
                  popper: { sx: { zIndex: CALL_LOG_PICKER_Z_INDEX } },
                  dialog: { sx: { zIndex: CALL_LOG_PICKER_Z_INDEX } },
                }}
              />
            </Column>
          ) : null}
          <Row sx={{ gap: 1.5, flexWrap: "wrap" }}>
            <MissedYouSmsButton
              candidate={candidate}
              visible={isConcierge(profile) && disposition === "no_answer"}
            />
            <MissedYouEmailButton
              candidate={candidate}
              visible={isConcierge(profile) && disposition === "no_answer"}
            />
          </Row>
          <TextField
            label="Notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            multiline
            minRows={3}
            maxRows={6}
            fullWidth
            placeholder="Add a short note about this attempt"
          />
          {saveError ? (
            <Typography variant="body2" color="error">
              {saveError}
            </Typography>
          ) : null}
        </Column>
      </DialogContent>
      <DialogActions sx={sidePanel ? { flexShrink: 0 } : undefined}>
        <Button variant="outlined" color="inherit" onClick={handleSoftClose}>
          Skip
        </Button>
        <LoadingButton
          variant="contained"
          color="primary"
          loading={isSaving}
          disabled={
            !disposition ||
            (disposition === "callback" && (!callbackStart || !callbackEnd))
          }
          onClick={handleSave}
        >
          Save call log
        </LoadingButton>
      </DialogActions>
    </>
  )

  if (sidePanel) {
    return (
      <Portal>
        <Slide direction="left" in={open} mountOnEnter unmountOnExit>
          <Paper
            role="dialog"
            aria-label="Log call attempt"
            elevation={12}
            sx={{
              position: "fixed",
              top: 0,
              bottom: 0,
              // Sits flush against the Power Dialer sidebar when one is open.
              // The sidebar variable is a desktop pixel value, so on xs the
              // panel covers the screen instead of being pushed off-canvas.
              right: { xs: 0, sm: "var(--power-dialer-sidebar-width, 0px)" },
              width: { xs: "100%", sm: CALL_LOG_PANEL_WIDTH },
              maxWidth: {
                xs: "100vw",
                sm: "calc(100vw - var(--power-dialer-sidebar-width, 0px))",
              },
              zIndex: CALL_LOG_DIALOG_Z_INDEX,
              display: "flex",
              flexDirection: "column",
              borderRadius: 0,
              borderLeft: "1px solid",
              borderColor: "colors.brand200",
            }}
          >
            {body}
          </Paper>
        </Slide>
      </Portal>
    )
  }

  return (
    <Dialog
      open={open}
      disableEscapeKeyDown
      onClose={(_, reason) => {
        if (reason === "backdropClick" || reason === "escapeKeyDown") {
          return
        }
      }}
      maxWidth="sm"
      fullWidth
      sx={{ zIndex: CALL_LOG_DIALOG_Z_INDEX }}
    >
      {body}
    </Dialog>
  )
}
